/**
 * A readable stand-in for the scrollbar a touch browser hides.
 *
 * Shared rather than written per section: a carousel's progress bar is the
 * same object wherever it appears, and two copies would drift apart the way
 * the collection list and the collection carousel did.
 *
 * Usage:
 *   <scroll-indicator data-rail=".selector-of-the-scrolling-element" hidden>
 *     <span class="scroll-indicator__thumb"></span>
 *   </scroll-indicator>
 *
 * The rail is looked up among its own siblings, so the indicator can sit
 * anywhere alongside the thing it describes.
 */
class ScrollIndicator extends HTMLElement {
  connectedCallback() {
    this.rail = this.parentElement?.querySelector(this.dataset.rail);
    this.thumb = this.querySelector('.scroll-indicator__thumb');
    if (!this.rail || !this.thumb) return;

    this.update = this.update.bind(this);
    this.rail.addEventListener('scroll', this.update, { passive: true });

    // The rail's width, and whether it overflows at all, both change with the
    // breakpoint — so this is re-measured rather than worked out once.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.rail);
    }

    this.update();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.update);
    this.observer?.disconnect();
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.rail;
    const max = scrollWidth - clientWidth;

    // Nothing to scroll means nothing to indicate.
    this.hidden = max <= 1;
    if (this.hidden) return;

    const visible = clientWidth / scrollWidth;
    this.thumb.style.setProperty('--thumb-width', `${visible * 100}%`);
    this.thumb.style.setProperty(
      '--thumb-offset',
      `${(scrollLeft / max) * (100 - visible * 100)}%`
    );
  }
}

if (!customElements.get('scroll-indicator')) {
  customElements.define('scroll-indicator', ScrollIndicator);
}
